// @ts-nocheck
/**
 * Role-picker for the Create User form. Computes the set of target roles
 * the current creator can create — full logic ported verbatim from v1
 * UserAddPage's role-dropdown gating (§2 of the semantic inventory):
 *
 *   • isOwnCompany + isCompanyAdmin → only CALL_CENTRE_ADMIN
 *   • isSuperAdmin                  → SUB_SUPERADMIN, RESELLER
 *   • isCompanyAdmin + !isOwnCompany:
 *       - 3-level chain all COMPANY_ADMIN → only CALL_CENTRE_ADMIN
 *       - else                             → COMPANY_ADMIN + CALL_CENTRE_ADMIN
 *   • isCallCenterAdmin             → CALL_CENTRE_TEAM_LEAD
 *   • isReseller + isOwnCompany     → CALL_CENTRE_ADMIN
 *   • isReseller + !isOwnCompany:
 *       - 3-level chain all RESELLER → only COMPANY_ADMIN
 *       - else                        → RESELLER + COMPANY_ADMIN
 *
 * The dead-code `role_ccagent` branch under CALL_CENTRE_ADMIN is
 * deliberately dropped — see catalog §2.
 *
 * Rendered as a radio-tile grid (v1 CreateUserPage pattern): each option is
 * a bordered card with a radio dot, label, and short hint — instead of a
 * dropdown. Users see all available roles at a glance.
 */
import { useMemo } from 'react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils/cn';
import { USER_ROLES, USERS_ROLE } from '@/utilities/constant';

interface Props {
  value: string;
  onChange: (role: string) => void;
  creatorRole: string;
  isOwnCompany: boolean;
  isAllCompanyAdminChain: boolean;
  isAllResellerChain: boolean;
  disabled?: boolean;
}

interface Option {
  value: string;
  label: string;
  hint: string;
}

const ROLE_HINTS: Record<string, string> = {
  [USER_ROLES.SUB_SUPERADMIN]: 'Platform admin with narrower scope than super-admin.',
  [USER_ROLES.RESELLER]: 'Sub-admin with their own end users underneath you.',
  [USER_ROLES.COMPANY_ADMIN]: 'End customer — runs campaigns directly.',
  [USER_ROLES.CALL_CENTRE_ADMIN]: 'Manages a call center, its team leads and agents.',
  [USER_ROLES.CALL_CENTRE_TEAM_LEAD]: 'Supervises a group of agents inside a call center.',
  [USER_ROLES.CALL_CENTRE_AGENT]: 'Front-line agent placing / receiving calls.',
};

function computeOptions(
  creatorRole: string,
  isOwnCompany: boolean,
  isAllCompanyAdminChain: boolean,
  isAllResellerChain: boolean,
): Option[] {
  const opt = (role: string): Option => ({
    value: role,
    label: USERS_ROLE[role] || role,
    hint: ROLE_HINTS[role] || '',
  });

  const isSuperAdmin = creatorRole === USER_ROLES.SUPERADMIN || creatorRole === USER_ROLES.SUB_SUPERADMIN;
  const isReseller = creatorRole === USER_ROLES.RESELLER;
  const isCompanyAdmin = creatorRole === USER_ROLES.COMPANY_ADMIN;
  const isCallCentreAdmin = creatorRole === USER_ROLES.CALL_CENTRE_ADMIN;

  if (isCompanyAdmin && isOwnCompany) return [opt(USER_ROLES.CALL_CENTRE_ADMIN)];
  if (isSuperAdmin) return [opt(USER_ROLES.SUB_SUPERADMIN), opt(USER_ROLES.RESELLER)];
  if (isCompanyAdmin && !isOwnCompany) {
    if (isAllCompanyAdminChain) return [opt(USER_ROLES.CALL_CENTRE_ADMIN)];
    return [opt(USER_ROLES.COMPANY_ADMIN), opt(USER_ROLES.CALL_CENTRE_ADMIN)];
  }
  if (isCallCentreAdmin) return [opt(USER_ROLES.CALL_CENTRE_TEAM_LEAD)];
  if (isReseller && isOwnCompany) return [opt(USER_ROLES.CALL_CENTRE_ADMIN)];
  if (isReseller && !isOwnCompany) {
    if (isAllResellerChain) return [opt(USER_ROLES.COMPANY_ADMIN)];
    return [opt(USER_ROLES.RESELLER), opt(USER_ROLES.COMPANY_ADMIN)];
  }
  return [];
}

export function RolePicker({
  value,
  onChange,
  creatorRole,
  isOwnCompany,
  isAllCompanyAdminChain,
  isAllResellerChain,
  disabled,
}: Props) {
  const options = useMemo(
    () => computeOptions(creatorRole, isOwnCompany, isAllCompanyAdminChain, isAllResellerChain),
    [creatorRole, isOwnCompany, isAllCompanyAdminChain, isAllResellerChain],
  );

  if (options.length === 0) {
    return (
      <div className="rounded-md border bg-card p-3 text-sm text-muted-foreground">
        No roles available for your account.
      </div>
    );
  }

  return (
    <RadioGroup
      value={value || ''}
      onValueChange={onChange}
      disabled={disabled}
      className="grid grid-cols-1 gap-2 sm:grid-cols-2"
    >
      {options.map((opt) => {
        const selected = value === opt.value;
        return (
          <label
            key={opt.value}
            htmlFor={`role-${opt.value}`}
            className={cn(
              'flex cursor-pointer items-start gap-3 rounded-md border bg-card p-3 transition-colors',
              selected ? 'border-primary ring-1 ring-primary/40' : 'hover:border-ring',
              disabled && 'cursor-not-allowed opacity-60',
            )}
          >
            <RadioGroupItem
              value={opt.value}
              id={`role-${opt.value}`}
              className="mt-0.5"
            />
            <div className="min-w-0">
              <div className="text-sm font-medium">{opt.label}</div>
              {opt.hint && (
                <div className="mt-0.5 text-xs text-muted-foreground">{opt.hint}</div>
              )}
            </div>
          </label>
        );
      })}
    </RadioGroup>
  );
}
